
import { Injectable, computed, Signal } from '@angular/core';
import { Query } from '@datorama/akita';
import { AuthStore, AuthState } from './auth.store';
import { toSignal } from '@angular/core/rxjs-interop';
import { Observable } from 'rxjs';
import {ValidationError} from '../../models/auth.models';

@Injectable({ providedIn: 'root' })
export class AuthQuery extends Query<AuthState> {
  // Declare properties (don't initialize)
  user$!: Observable<any>;
  tokens$!: Observable<any>;
  isAuthenticated$!: Observable<boolean>;
  loading$!: Observable<boolean>;
  error$!: Observable<string | null>;
  validationErrors$!: Observable<ValidationError[]>;

  user!: Signal<any>;
  tokens!: Signal<any>;
  isAuthenticated!: Signal<boolean>;
  loading!: Signal<boolean>;
  error!: Signal<string | null>;

  fullName!: Signal<string>;
  userEmail!: Signal<string>;
  hasValidToken!: Signal<boolean>;
  validationErrors!: Signal<ValidationError[]>;

  constructor(protected override store: AuthStore) {
    super(store);

    // Initialize observables
    this.user$ = this.select(state => state.user);
    this.tokens$ = this.select(state => state.tokens);
    this.isAuthenticated$ = this.select(state => state.isAuthenticated);
    this.loading$ = this.select(state => state.loading);
    this.error$ = this.select(state => state.error);
    this.validationErrors$ = this.select(state => state.validationErrors);

    // Initialize signals
    this.user = toSignal(this.user$, { initialValue: null });
    this.tokens = toSignal(this.tokens$, { initialValue: null });
    this.isAuthenticated = toSignal(this.isAuthenticated$, { initialValue: false });
    this.loading = toSignal(this.loading$, { initialValue: false });
    this.error = toSignal(this.error$, { initialValue: null });
    this.validationErrors = toSignal(this.validationErrors$, { initialValue: [] });

    // Initialize computed signals
    this.fullName = computed(() => {
      const user = this.user();
      return user ? `${user.firstName} ${user.lastName}` : 'Guest';
    });

    this.userEmail = computed(() => this.user()?.email ?? '');
    this.hasValidToken = computed(() => !!this.tokens()?.access_token);
  }

  isLoggedIn(): boolean {
    return this.getValue().isAuthenticated;
  }

  getAccessToken(): string | null {
    return this.getValue().tokens?.access_token ?? null;
  }

  getRefreshToken(): string | null {
    return this.getValue().tokens?.refresh_token ?? null;
  }
}
